import logger from '../../log';
import {
  StdPlayerEvents,
  EventObject,
  MetadataTag,
  MonitoredVideoEvent
} from './constants';
import transformDuration from './transformDuration';
import {
  dispatchIfValidDuration,
  dispatchPlayerEvent
} from '../../events/dispatch';
import { Handler } from '@kollective-frontend/ksdk-api';
import { CleanupMethod } from '../../player/BaseInteraction';

const log = logger.getLogger(__filename);
// This is a subset of HTMLMediaElement, with only the information we need
// required.
// https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement
export interface AttachableElement {
  addEventListener: (name: string, handler: Handler<unknown>) => void;
  removeEventListener?: (name: string, handler?: Handler<unknown>) => void;
  playbackRate: number;
  duration: number;
  currentTime: number;
}

const attachVideoElement = (player: AttachableElement): CleanupMethod => {
  const getMetadataForEvent = (metadata?: MetadataTag) => {
    switch (metadata) {
      case 'rate':
        return { rate: player.playbackRate };
      case 'dur':
        return { dur: transformDuration(player.duration) };
      case 'pos':
        return { pos: player.currentTime };
      default:
        return;
    }
  };

  const generateHandler =
    (event: EventObject): Handler<Event> =>
    (): void => {
      const { code, metadata } = event;
      log.info('player event:', event);
      dispatchPlayerEvent({ e: code, ...getMetadataForEvent(metadata) });
    };

  // Try setting the duration immediately upon attachment
  dispatchIfValidDuration(transformDuration(player.duration));

  const handlers: Map<string, Handler<Event>> = new Map();
  Object.getOwnPropertyNames(StdPlayerEvents)
    .map(key => StdPlayerEvents[key as MonitoredVideoEvent])
    .forEach(event => {
      const handler = generateHandler(event);
      handlers.set(event.name, handler);
      player.addEventListener(event.name, handler as Handler<unknown>);
    });
  return () => {
    if (typeof player.removeEventListener !== 'function') {
      log.debug('Not able to unset handlers', player);
    } else {
      log.debug('Unsetting handlers', player);
      handlers.forEach((handler, name) => {
        if (typeof player.removeEventListener === 'function') {
          player.removeEventListener(name, handler as Handler<unknown>);
        }
      });
    }
    handlers.clear();
  };
};

export default attachVideoElement;
